// src/pages/workflow/RegistroEntidad.jsx
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  Button,
  Grid,
  Typography,
  Box,
  Divider,
  Avatar,
  Paper,
  Tooltip,
  IconButton,
  LinearProgress,
  Alert,
  AlertTitle,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Business as BusinessIcon,
  Dashboard as DashboardIcon,
  Home as HomeIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
  ContactPhone as ContactPhoneIcon,
  Group as GroupIcon,
  Contacts as ContactsIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Save as SaveIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Work as WorkIcon
} from '@mui/icons-material';
import { 
  validarRegistroCompleto
} from '../../redux/slices/workflowSlice';
import { registrarUsuario, obtenerRoles, obtenerUsuariosPorEntidad } from '../../actions/UserActions';
import { obtenerEntidadPorId } from '../../actions/EntidadAction';
import { 
  obtenerContactosPorEntidad, 
  crearContacto, 
  eliminarContacto, 
  obtenerCargos,
  actualizarContacto
} from '../../actions/ContactoAction';
import useStyles from '../../theme/useStyles';

const RegistroEntidad = () => {
  const { entidadId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const classes = useStyles();

  const { user } = useSelector((state) => state.session);

  // Estados principales
  const [entidadInfo, setEntidadInfo] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [contactos, setContactos] = useState([]);
  const [cargos, setCargos] = useState([]);
  const [roles, setRoles] = useState([]);
  const [validando, setValidando] = useState(false);
  const [cargandoEntidad, setCargandoEntidad] = useState(true);
  
  // Estados para modales
  const [mostrarFormUsuario, setMostrarFormUsuario] = useState(false);
  const [mostrarFormContacto, setMostrarFormContacto] = useState(false);
  
  // Estados para formularios
  const [nuevoUsuario, setNuevoUsuario] = useState({
    nombre: '',
    apellido: '',
    email: '',
    telefono: '',
    rolId: '',
    password: '',
    confirmarPassword: ''
  });
  
  const [erroresUsuario, setErroresUsuario] = useState({});
  const [guardandoUsuario, setGuardandoUsuario] = useState(false);
  
  const [nuevoContacto, setNuevoContacto] = useState({
    nombres: '',
    apellidos: '',
    email1: '',
    email2: '',
    celular1: '',
    celular2: '',
    cargoId: '', // Cambiar de '' a 0 o mantener como string pero ser consistente
    principal: false
  });

  const [erroresContacto, setErroresContacto] = useState({});
  const [guardandoContacto, setGuardandoContacto] = useState(false);

  const puedeAvanzar = usuarios.length > 0 && contactos.length > 0;

  useEffect(() => {
    if (entidadId) {
      cargarDatosEntidad();
      cargarRoles();
      cargarCargos();
    }
  }, [entidadId]);

  const cargarCargos = async () => {
    try {
      const response = await obtenerCargos();
      if (response.status === 200) {
        setCargos(response.data);
      }
    } catch (error) {
      console.error('Error cargando cargos:', error);
    }
  };

  const cargarRoles = async () => {
    try {
      const response = await obtenerRoles();
      if (response.status === 200) {
        setRoles(response.data);
      }
    } catch (error) {
      console.error('Error cargando roles:', error);
    }
  };

  const cargarDatosEntidad = async () => {
    setCargandoEntidad(true);
    try {
      // Cargar información de la entidad usando el action
      try {
        const entidadResponse = await obtenerEntidadPorId(entidadId);
        if (entidadResponse.status === 200) {
          setEntidadInfo(entidadResponse.data);
        }
      } catch (error) {
        console.error('Error cargando datos de la entidad:', error);
      }

      // Cargar usuarios reales de la entidad usando el action
      try {
        const response = await obtenerUsuariosPorEntidad(entidadId);
        if (response.status === 200) {
          setUsuarios(response.data);
        } else {
          console.error('Error cargando usuarios de la entidad');
          setUsuarios([]);
        }
      } catch (error) {
        console.error('Error en obtenerUsuariosPorEntidad:', error);
        setUsuarios([]);
      }
      
      // Cargar contactos reales de la entidad
      try {
        const contactosResponse = await obtenerContactosPorEntidad(entidadId);
        if (contactosResponse.status === 200) {
          setContactos(contactosResponse.data);
        } else {
          console.error('Error cargando contactos de la entidad');
          setContactos([]);
        }
      } catch (error) {
        console.error('Error en obtenerContactosPorEntidad:', error);
        setContactos([]);
      }
    } catch (error) {
      console.error('Error cargando datos de entidad:', error);
      setUsuarios([]);
      setContactos([]);
    } finally {
      setCargandoEntidad(false);
    }
  };

  const handleValidarRegistro = async () => {
    setValidando(true);
    try {
      const resultado = await dispatch(validarRegistroCompleto(entidadId));
      
      if (resultado.payload?.esExitoso) {
        // Hacer comparación más robusta (case-insensitive y booleana)
        const registroCompleto = resultado.payload.resultado === 'True' || 
                                  resultado.payload.resultado === true ||
                                  resultado.payload.resultado?.toLowerCase() === 'true';
        
        if (registroCompleto) {
          navigate(`/inscripcion/${entidadId}/documentos`);
        } else {
          alert('El registro aún no está completo. Asegúrese de tener al menos un usuario y un contacto registrados en la base de datos.');
        }
      } else {
        alert('Error en la validación. Intente nuevamente.');
      }
    } catch (error) {
      console.error('Error validando registro:', error);
      alert('Error al validar el registro. Intente nuevamente.');
    } finally {
      setValidando(false);
    }
  };

  const validarFormularioUsuario = () => {
    const nuevosErrores = {};
    
    if (!nuevoUsuario.nombre.trim()) nuevosErrores.nombre = 'El nombre es requerido';
    if (!nuevoUsuario.apellido.trim()) nuevosErrores.apellido = 'El apellido es requerido';
    if (!nuevoUsuario.email.trim()) {
      nuevosErrores.email = 'El email es requerido';
    } else if (!/\S+@\S+\.\S+/.test(nuevoUsuario.email)) {
      nuevosErrores.email = 'El email no es válido';
    }
    if (!nuevoUsuario.telefono.trim()) nuevosErrores.telefono = 'El teléfono es requerido';
    if (!nuevoUsuario.rolId) nuevosErrores.rolId = 'El rol es requerido';
    if (!nuevoUsuario.password.trim()) {
      nuevosErrores.password = 'La contraseña es requerida';
    } else if (nuevoUsuario.password.length < 6) {
      nuevosErrores.password = 'La contraseña debe tener al menos 6 caracteres';
    }
    if (!nuevoUsuario.confirmarPassword.trim()) {
      nuevosErrores.confirmarPassword = 'Debe confirmar la contraseña';
    } else if (nuevoUsuario.password !== nuevoUsuario.confirmarPassword) {
      nuevosErrores.confirmarPassword = 'Las contraseñas no coinciden';
    }
    
    setErroresUsuario(nuevosErrores);
    return Object.keys(nuevosErrores).length === 0;
  };

  const validarFormularioContacto = () => {
    const nuevosErrores = {};
    
    if (!nuevoContacto.nombres.trim()) nuevosErrores.nombres = 'Los nombres son requeridos';
    if (!nuevoContacto.apellidos.trim()) nuevosErrores.apellidos = 'Los apellidos son requeridos';
    if (!nuevoContacto.email1.trim()) {
      nuevosErrores.email1 = 'El email principal es requerido';
    } else if (!/\S+@\S+\.\S+/.test(nuevoContacto.email1)) {
      nuevosErrores.email1 = 'El email principal no es válido';
    }
    if (nuevoContacto.email2 && !/\S+@\S+\.\S+/.test(nuevoContacto.email2)) {
      nuevosErrores.email2 = 'El email secundario no es válido';
    }
    if (!nuevoContacto.celular1.trim()) nuevosErrores.celular1 = 'El teléfono principal es requerido';
    if (!nuevoContacto.cargoId || nuevoContacto.cargoId === '') nuevosErrores.cargoId = 'El cargo es requerido';
    
    setErroresContacto(nuevosErrores);
    return Object.keys(nuevosErrores).length === 0;
  };

  const resetFormularioUsuario = () => {
    setNuevoUsuario({
      nombre: '',
      apellido: '',
      email: '',
      telefono: '',
      rolId: '',
      password: '',
      confirmarPassword: ''
    });
    setErroresUsuario({});
  };

  const resetFormularioContacto = () => {
    setNuevoContacto({
      nombres: '',
      apellidos: '',
      email1: '',
      email2: '',
      celular1: '',
      celular2: '',
      cargoId: '', // Mantener consistencia
      principal: false
    });
    setErroresContacto({});
  };

  const handleGuardarUsuario = async () => {
    if (!validarFormularioUsuario()) return;
    
    setGuardandoUsuario(true);
    
    try {
      // Buscar el nombre del rol seleccionado
      const rolSeleccionado = roles.find(r => r.id === nuevoUsuario.rolId);
      
      const usuarioData = {
        entidadID: parseInt(entidadId),
        email: nuevoUsuario.email,
        userName: nuevoUsuario.email, // Usando email como userName
        nombre: nuevoUsuario.nombre,
        apellido: nuevoUsuario.apellido,
        telefono: nuevoUsuario.telefono,
        password: nuevoUsuario.password,
        rol: rolSeleccionado ? rolSeleccionado.nombre : '',
        imagen: '' // Campo vacío por ahora
      };

      const response = await registrarUsuario(usuarioData);

      if (response.status === 200 || response.status === 201) {
        // Recargar usuarios desde el API usando el action
        try {
          const usuariosResponse = await obtenerUsuariosPorEntidad(entidadId);
          if (usuariosResponse.status === 200) {
            setUsuarios(usuariosResponse.data);
          }
        } catch (error) {
          console.error('Error recargando usuarios:', error);
          // Si falla la recarga, mantener la lista actual
        }
        
        setMostrarFormUsuario(false);
        resetFormularioUsuario();
        alert('Usuario creado exitosamente');
      } else {
        console.error('Error del servidor:', response.data);
        alert(response.data?.message || 'Error al crear el usuario. Intente nuevamente.');
      }
    } catch (error) {
      console.error('Error creando usuario:', error);
      alert('Error al crear el usuario. Intente nuevamente.');
    } finally {
      setGuardandoUsuario(false);
    }
  };

  const handleGuardarContacto = async () => {
    if (!validarFormularioContacto()) return;
    
    setGuardandoContacto(true);
    
    try {
      const contactoData = {
        entidadID: parseInt(entidadId),
        nombres: nuevoContacto.nombres,
        apellidos: nuevoContacto.apellidos,
        email1: nuevoContacto.email1,
        email2: nuevoContacto.email2 || null,
        celular1: nuevoContacto.celular1,
        celular2: nuevoContacto.celular2 || null,
        cargoID: nuevoContacto.cargoId, // El estado interno sigue siendo cargoId, pero el API espera cargoID
        principal: nuevoContacto.principal
      };

      const response = await crearContacto(contactoData);

      if (response.status === 200 || response.status === 201) {
        // Recargar contactos desde el API
        try {
          const contactosResponse = await obtenerContactosPorEntidad(entidadId);
          if (contactosResponse.status === 200) {
            setContactos(contactosResponse.data);
          }
        } catch (error) {
          console.error('Error recargando contactos:', error);
          // Si falla la recarga, mantener la lista actual
        }
        
        setMostrarFormContacto(false);
        resetFormularioContacto();
        alert('Contacto creado exitosamente');
      } else {
        console.error('Error del servidor:', response.data);
        alert(response.data?.message || 'Error al crear el contacto. Intente nuevamente.');
      }
    } catch (error) {
      console.error('Error creando contacto:', error);
      alert('Error al crear el contacto. Intente nuevamente.');
    } finally {
      setGuardandoContacto(false);
    }
  };

  const handleEliminarUsuario = async (id) => {
    if (window.confirm('¿Está seguro de que desea eliminar este usuario?')) {
      try {
        // Aquí iría la llamada al API para eliminar el usuario
        // Por ahora solo eliminamos de la lista local
        setUsuarios(usuarios.filter(u => u.id !== id));
        
        // TODO: Implementar eliminación en el backend
        // await eliminarUsuario(id);
        // Recargar lista después de eliminar
        
        alert('Usuario eliminado exitosamente');
      } catch (error) {
        console.error('Error eliminando usuario:', error);
        alert('Error al eliminar el usuario');
      }
    }
  };

  const handleEliminarContacto = async (id) => {
    if (window.confirm('¿Está seguro de que desea eliminar este contacto?')) {
      try {
        const response = await eliminarContacto(id);
        
        if (response.status === 200 || response.status === 204) {
          // Recargar contactos desde el API
          try {
            const contactosResponse = await obtenerContactosPorEntidad(entidadId);
            if (contactosResponse.status === 200) {
              setContactos(contactosResponse.data);
            }
          } catch (error) {
            console.error('Error recargando contactos después de eliminar:', error);
            // Si falla la recarga, eliminar de la lista local
            setContactos(contactos.filter(c => c.contactoEntidadID !== id));
          }
          
          alert('Contacto eliminado exitosamente');
        } else {
          alert('Error al eliminar el contacto. Intente nuevamente.');
        }
      } catch (error) {
        console.error('Error eliminando contacto:', error);
        alert('Error al eliminar el contacto. Intente nuevamente.');
      }
    }
  };

  // Componente para mostrar usuario
  const UserCard = ({ usuario, onEliminar }) => (
    <Paper 
      elevation={1} 
      sx={{ 
        p: 2, 
        mb: 1, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between' 
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
        <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
          <PersonAddIcon />
        </Avatar>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
            {usuario.nombre}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
            <EmailIcon sx={{ fontSize: 14, mr: 0.5 }} />
            {usuario.email}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
            <PhoneIcon sx={{ fontSize: 14, mr: 0.5 }} />
            {usuario.telefono}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Chip 
          label={usuario.rol} 
          size="small" 
          color="primary"
          variant="outlined"
        />
        {usuario.activo && (
          <Chip 
            label="Activo" 
            size="small" 
            color="success"
            variant="outlined"
          />
        )}
        <Box>
          <Tooltip title="Editar">
            <IconButton size="small" color="primary">
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton 
              size="small" 
              color="error"
              onClick={() => onEliminar(usuario.id)}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Paper>
  );

  // Componente para mostrar contacto
  const ContactCard = ({ contacto, onEliminar }) => {
    const nombreCompleto = `${contacto.nombres} ${contacto.apellidos}`.trim();
    // Manejar el nombre del cargo según la estructura que viene del API
    const cargoNombre = contacto.cargo?.nombre || contacto.cargoNombre || 'Sin cargo';
    
    return (
      <Paper 
        elevation={1} 
        sx={{ 
          p: 2, 
          mb: 1, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between' 
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <Avatar sx={{ bgcolor: 'secondary.main', mr: 2 }}>
            <ContactPhoneIcon />
          </Avatar>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              {nombreCompleto}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
              <EmailIcon sx={{ fontSize: 14, mr: 0.5 }} />
              {contacto.email1}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
              <PhoneIcon sx={{ fontSize: 14, mr: 0.5 }} />
              {contacto.celular1}
            </Typography>
            {contacto.email2 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                <EmailIcon sx={{ fontSize: 12, mr: 0.5 }} />
                {contacto.email2}
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip 
            label={cargoNombre} 
            size="small" 
            color="secondary"
            variant="outlined"
          />
          {contacto.principal && (
            <Chip 
              label="Principal" 
              size="small" 
              color="primary"
              variant="outlined"
            />
          )}
          <Box>
            <Tooltip title="Editar">
              <IconButton size="small" color="primary">
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Eliminar">
              <IconButton 
                size="small" 
                color="error"
                onClick={() => onEliminar(contacto.contactoEntidadID)}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Paper>
    );
  };

  return (
    <Box sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
      minHeight: '100vh',
      p: 2,
      boxSizing: 'border-box'
    }}>
      <Card className={classes.card}
        sx={{ 
          width: '100%',
          maxWidth: 1200,
          margin: 'auto',
          mt: 4,
          p: 2
        }}
      >
        <CardHeader 
          classes={{ title: classes.cardTitle }}
          avatar={
            <BusinessIcon style={{ fontSize: "large", color: "#494949" }} />
          }
          title="Registro de Entidad" 
          subheader={
            cargandoEntidad 
              ? "Cargando información de la entidad..." 
              : entidadInfo 
                ? `Complete el registro de "${entidadInfo.nombre}" con usuarios y contactos`
                : "Complete el registro con usuarios y contactos de la entidad"
          }
          action={
            <Grid container spacing={1} alignItems="center" justifyContent="center">
              <Grid item key="dashboard-button">
                <Tooltip title="Volver al Dashboard" arrow classes={{ tooltip: classes.tooltip }}>
                  <IconButton
                    onClick={() => navigate(`/inscripcion/${entidadId}`)}
                    variant="contained"
                    color="secondary"
                    size="small"
                    className={classes.button}
                  >
                    <DashboardIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
              <Grid item key="home-button">
                <Tooltip title="Ir al Inicio" arrow classes={{ tooltip: classes.tooltip }}>
                  <IconButton
                    onClick={() => navigate('/')}
                    variant="contained"
                    color="secondary"
                    size="small"
                    className={classes.button}
                  >
                    <HomeIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          }
          sx={{ 
            '& .MuiCardHeader-title': { fontSize: '1.25rem' },
            pr: 6
          }}
        />

        <CardContent>
          {cargandoEntidad ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <CircularProgress size={40} sx={{ mr: 2 }} />
              <Typography variant="body1" color="text.secondary">
                Cargando datos de la entidad...
              </Typography>
            </Box>
          ) : (
            <>
              {/* Progreso */}
              <Box sx={{ mb: 3 }}>
                <Paper elevation={1} sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                    Progreso del Registro
                  </Typography>
                  
                  <Stepper orientation="horizontal" sx={{ mb: 2 }}>
                    <Step key="step-usuarios" completed={usuarios.length > 0}>
                      <StepLabel>
                        <Typography variant="body2" sx={{ fontWeight: usuarios.length > 0 ? 'bold' : 'normal' }}>
                          Usuarios ({usuarios.length})
                        </Typography>
                      </StepLabel>
                    </Step>
                    <Step key="step-contactos" completed={contactos.length > 0}>
                      <StepLabel>
                        <Typography variant="body2" sx={{ fontWeight: contactos.length > 0 ? 'bold' : 'normal' }}>
                          Contactos ({contactos.length})
                        </Typography>
                      </StepLabel>
                    </Step>
                  </Stepper>
                  
                  <LinearProgress 
                    variant="determinate" 
                    value={puedeAvanzar ? 100 : (usuarios.length > 0 || contactos.length > 0) ? 50 : 0}
                    sx={{ mt: 2, height: 6, borderRadius: 3 }}
                    color={puedeAvanzar ? 'success' : 'primary'}
                  />
                </Paper>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Secciones de Usuarios y Contactos */}
              <Grid container spacing={3}>
                {/* Usuarios */}
                <Grid item size={{ xs: 12, lg: 6 }} key="usuarios-section">
                  <Paper elevation={1} sx={{ height: 'fit-content' }}>
                    <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <GroupIcon sx={{ mr: 1, color: 'primary.main' }} />
                          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            Usuarios de la Entidad
                          </Typography>
                        </Box>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => setMostrarFormUsuario(true)}
                        >
                          Agregar
                        </Button>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Se requiere al menos un usuario administrador
                      </Typography>
                    </Box>

                    <Box sx={{ p: 3, minHeight: 300 }}>
                      {usuarios.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                          <GroupIcon sx={{ fontSize: 60, color: 'grey.300', mb: 2 }} />
                          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                            No hay usuarios registrados
                          </Typography>
                          <Button
                            variant="outlined"
                            onClick={() => setMostrarFormUsuario(true)}
                            startIcon={<AddIcon />}
                          >
                            Agregar el primer usuario
                          </Button>
                        </Box>
                      ) : (
                        <Box>
                          {usuarios.map(usuario => (
                            <UserCard 
                              key={`usuario-${usuario.id}`}
                              usuario={usuario}
                              onEliminar={handleEliminarUsuario}
                            />
                          ))}
                        </Box>
                      )}
                    </Box>
                  </Paper>
                </Grid>

                {/* Contactos */}
                <Grid item size={{ xs: 12, lg: 6 }} key="contactos-section">
                  <Paper elevation={1} sx={{ height: 'fit-content' }}>
                    <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <ContactsIcon sx={{ mr: 1, color: 'secondary.main' }} />
                          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            Contactos de la Entidad
                          </Typography>
                        </Box>
                        <Button
                          variant="contained"
                          color="secondary"
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => setMostrarFormContacto(true)}
                        >
                          Agregar
                        </Button>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Se requiere al menos un contacto principal
                      </Typography>
                    </Box>

                    <Box sx={{ p: 3, minHeight: 300 }}>
                      {contactos.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                          <ContactsIcon sx={{ fontSize: 60, color: 'grey.300', mb: 2 }} />
                          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                            No hay contactos registrados
                          </Typography>
                          <Button
                            variant="outlined"
                            color="secondary"
                            onClick={() => setMostrarFormContacto(true)}
                            startIcon={<AddIcon />}
                          >
                            Agregar el primer contacto
                          </Button>
                        </Box>
                      ) : (
                        <Box>
                          {contactos.map((contacto, index) => (
                            <ContactCard 
                              key={contacto.contactoEntidadID ? `contacto-${contacto.contactoEntidadID}` : `contacto-temp-${index}`}
                              contacto={contacto}
                              onEliminar={handleEliminarContacto}
                            />
                          ))}
                        </Box>
                      )}
                    </Box>
                  </Paper>
                </Grid>
              </Grid>

              {/* Botón de continuar */}
              <Paper elevation={2} sx={{ p: 3, mt: 3, bgcolor: 'background.default' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                      ¿Completó el registro?
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Debe tener al menos un usuario administrador y un contacto principal
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      onClick={cargarDatosEntidad}
                      disabled={cargandoEntidad}
                      size="small"
                    >
                      Recargar
                    </Button>
                    <Button
                      variant="contained"
                      color="success"
                      onClick={handleValidarRegistro}
                      disabled={!puedeAvanzar || validando}
                      startIcon={validando ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                      sx={{ minWidth: 180 }}
                    >
                      {validando ? 'Validando...' : 'Validar y Continuar'}
                    </Button>
                  </Box>
                </Box>
              </Paper>
            </>
          )}
        </CardContent>

        {/* Modal para formulario de usuario */}
        <Dialog 
          open={mostrarFormUsuario} 
          onClose={() => setMostrarFormUsuario(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PersonAddIcon sx={{ mr: 1 }} />
              Agregar Usuario
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Complete la información del nuevo usuario de la entidad
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Nombre"
                  value={nuevoUsuario.nombre}
                  onChange={(e) => setNuevoUsuario({...nuevoUsuario, nombre: e.target.value})}
                  error={!!erroresUsuario.nombre}
                  helperText={erroresUsuario.nombre}
                  required
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Apellido"
                  value={nuevoUsuario.apellido}
                  onChange={(e) => setNuevoUsuario({...nuevoUsuario, apellido: e.target.value})}
                  error={!!erroresUsuario.apellido}
                  helperText={erroresUsuario.apellido}
                  required
                />
              </Grid>
              
              <Grid item size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Correo Electrónico"
                  type="email"
                  value={nuevoUsuario.email}
                  onChange={(e) => setNuevoUsuario({...nuevoUsuario, email: e.target.value})}
                  error={!!erroresUsuario.email}
                  helperText={erroresUsuario.email}
                  required
                />
              </Grid>
              
              <Grid item size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Teléfono"
                  value={nuevoUsuario.telefono}
                  onChange={(e) => setNuevoUsuario({...nuevoUsuario, telefono: e.target.value})}
                  error={!!erroresUsuario.telefono}
                  helperText={erroresUsuario.telefono}
                  placeholder="3001234567"
                  required
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth required error={!!erroresUsuario.rolId}>
                  <InputLabel>Rol</InputLabel>
                  <Select
                    value={nuevoUsuario.rolId || ''} // Asegurar que nunca sea undefined
                    onChange={(e) => setNuevoUsuario({...nuevoUsuario, rolId: e.target.value})}
                    label="Rol"
                  >
                    <MenuItem value="">
                      <em>Seleccione un rol...</em>
                    </MenuItem>
                    {roles.length === 0 ? (
                      <MenuItem disabled>
                        <em>Cargando roles...</em>
                      </MenuItem>
                    ) : (
                      roles.map((rol, index) => (
                        <MenuItem 
                          key={rol.id ? `rol-${rol.id}` : `rol-idx-${index}`} 
                          value={rol.id}
                        >
                          {rol.nombre || 'Sin nombre'}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {erroresUsuario.rolId && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, mx: 1.5 }}>
                      {erroresUsuario.rolId}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              
              <Grid item size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Contraseña"
                  type="password"
                  value={nuevoUsuario.password}
                  onChange={(e) => setNuevoUsuario({...nuevoUsuario, password: e.target.value})}
                  error={!!erroresUsuario.password}
                  helperText={erroresUsuario.password}
                  required
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Confirmar Contraseña"
                  type="password"
                  value={nuevoUsuario.confirmarPassword}
                  onChange={(e) => setNuevoUsuario({...nuevoUsuario, confirmarPassword: e.target.value})}
                  error={!!erroresUsuario.confirmarPassword}
                  helperText={erroresUsuario.confirmarPassword}
                  required
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => {
                setMostrarFormUsuario(false);
                resetFormularioUsuario();
              }}
              startIcon={<CancelIcon />}
              disabled={guardandoUsuario}
            >
              Cancelar
            </Button>
            <Button 
              variant="contained" 
              onClick={handleGuardarUsuario}
              disabled={guardandoUsuario}
              startIcon={guardandoUsuario ? <CircularProgress size={20} /> : <SaveIcon />}
            >
              {guardandoUsuario ? 'Guardando...' : 'Guardar Usuario'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal para formulario de contacto */}
        <Dialog 
          open={mostrarFormContacto} 
          onClose={() => setMostrarFormContacto(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ContactPhoneIcon sx={{ mr: 1 }} />
              Agregar Contacto
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Complete la información del nuevo contacto de la entidad
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Nombres"
                  value={nuevoContacto.nombres}
                  onChange={(e) => setNuevoContacto({...nuevoContacto, nombres: e.target.value})}
                  error={!!erroresContacto.nombres}
                  helperText={erroresContacto.nombres}
                  required
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Apellidos"
                  value={nuevoContacto.apellidos}
                  onChange={(e) => setNuevoContacto({...nuevoContacto, apellidos: e.target.value})}
                  error={!!erroresContacto.apellidos}
                  helperText={erroresContacto.apellidos}
                  required
                />
              </Grid>
              
              <Grid item size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Email Principal"
                  type="email"
                  value={nuevoContacto.email1}
                  onChange={(e) => setNuevoContacto({...nuevoContacto, email1: e.target.value})}
                  error={!!erroresContacto.email1}
                  helperText={erroresContacto.email1}
                  required
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Email Secundario"
                  type="email"
                  value={nuevoContacto.email2}
                  onChange={(e) => setNuevoContacto({...nuevoContacto, email2: e.target.value})}
                  error={!!erroresContacto.email2}
                  helperText={erroresContacto.email2}
                />
              </Grid>
              
              <Grid item size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Teléfono Principal"
                  value={nuevoContacto.celular1}
                  onChange={(e) => setNuevoContacto({...nuevoContacto, celular1: e.target.value})}
                  error={!!erroresContacto.celular1}
                  helperText={erroresContacto.celular1}
                  placeholder="3001234567"
                  required
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Teléfono Secundario"
                  value={nuevoContacto.celular2}
                  onChange={(e) => setNuevoContacto({...nuevoContacto, celular2: e.target.value})}
                  placeholder="3001234567"
                />
              </Grid>
              
              <Grid item size={{ xs: 12, sm: 8 }}>
                <FormControl fullWidth required error={!!erroresContacto.cargoId}>
                  <InputLabel>Cargo</InputLabel>
                  <Select
                    value={nuevoContacto.cargoId || ''} // Asegurar que nunca sea undefined
                    onChange={(e) => setNuevoContacto({...nuevoContacto, cargoId: e.target.value})}
                    label="Cargo"
                  >
                    <MenuItem value="">
                      <em>Seleccione un cargo...</em>
                    </MenuItem>
                    {cargos.length === 0 ? (
                      <MenuItem disabled>
                        <em>Cargando cargos...</em>
                      </MenuItem>
                    ) : (
                      cargos.map((cargo, index) => (
                        <MenuItem 
                          key={`cargo-${cargo.cargoID}`} 
                          value={cargo.cargoID}
                        >
                          {cargo.nombre}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {erroresContacto.cargoId && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, mx: 1.5 }}>
                      {erroresContacto.cargoId}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item size={{ xs: 12, sm: 4 }}>
                <FormControl fullWidth>
                  <InputLabel>Es Principal</InputLabel>
                  <Select
                    value={nuevoContacto.principal}
                    onChange={(e) => setNuevoContacto({...nuevoContacto, principal: e.target.value})}
                    label="Es Principal"
                  >
                    <MenuItem value={false}>No</MenuItem>
                    <MenuItem value={true}>Sí</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => {
                setMostrarFormContacto(false);
                resetFormularioContacto();
              }}
              startIcon={<CancelIcon />}
              disabled={guardandoContacto}
            >
              Cancelar
            </Button>
            <Button 
              variant="contained" 
              color="secondary"
              onClick={handleGuardarContacto}
              disabled={guardandoContacto}
              startIcon={guardandoContacto ? <CircularProgress size={20} /> : <SaveIcon />}
            >
              {guardandoContacto ? 'Guardando...' : 'Guardar Contacto'}
            </Button>
          </DialogActions>
        </Dialog>
      </Card>
    </Box>
  );
};

export default RegistroEntidad;